import React, { useEffect, useState } from 'react'
import { Grid, Input, InputLabel, Spinner, useTheme, useTranslation } from '@paintscout/ui'
import { DropdownSelect } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import { useAddProcessingPaymentMutation } from '@paintscout/api'
import type { PayrixTxnMetadata } from 'paintscout'
import { getPayrixCredentials, randomString } from '@paintscout/util/util'
import { useSnackbar } from 'notistack'
import { createLogger } from '@paintscout/logger'
import { usePayrixStyles } from './usePayrixStyles'

const log = createLogger('credit-card-form')
import type { FormProps } from './CollectPayrixPaymentDialog'

export default function CreditCardForm(props: FormProps) {
  const classes = usePayrixStyles()
  const { __ } = useTranslation()

  const {
    amount, // dollars including surcharge
    companyId,
    handleSuccessfulPayment,
    merchantId,
    note,
    options,
    payFieldsLoading,
    paymentRequestId,
    quote,
    setActivityId,
    setPayFieldsLoading,
    setSubmitting,
    source,
    surcharge // dollars
  } = props
  const [country, setCountry] = useState<DropdownSelectOption>(
    options.options.companyCountry?.value === 'Canada'
      ? { label: __('Canada'), value: 'CAN' }
      : { label: __('United States'), value: 'USA' }
  )
  const [zipError, setZipError] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const [addProcessingPaymentMutation] = useAddProcessingPaymentMutation()

  function handleZipError(error: string) {
    setZipError(error)
    setSubmitting(false)
    setPayFieldsLoading(false)
  }

  useEffect(() => {
    setZipError('')
    let submitButton
    let handleSubmit
    let activityId
    const { publicKey, apiUrl } = getPayrixCredentials(options)

    const script = document.createElement('script')
    script.src = `${apiUrl}/payFieldsScript?spa=1`
    script.type = 'text/javascript'
    script.async = true
    script.onload = () => {
      const PayFields = (window as any).PayFields as any
      if (!PayFields) {
        console.log('PayFields not found')
        return
      }
      PayFields.config.apiKey = publicKey
      PayFields.config.merchant = merchantId
      // we can't add a surcharge with PayFields directly, so we need to create a token and then create a txn from that token
      const useToken = surcharge > 0
      PayFields.config.mode = useToken ? 'token' : 'txn'
      PayFields.config.txnType = useToken ? 'capture' : 'sale'
      PayFields.config.amount = Math.round(amount * 100)

      if (!useToken) {
        PayFields.config.order = quote._id
      }

      PayFields.fields = [
        {
          type: 'number',
          element: '#number'
        },
        {
          type: 'name',
          element: '#name',
          values: {
            name: quote.contact ? `${quote.contact.first_name} ${quote.contact.last_name}` : null
          }
        },
        {
          type: 'cvv',
          element: '#cvv'
        },
        {
          type: 'expiration',
          element: '#expiration'
        }
      ]
      PayFields.customizations.style = {
        '.input': {
          ...theme.typography.body1,
          border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
          borderRadius: theme.borderRadius.md,
          boxSizing: 'border-box',
          height: '40px',
          padding: '10px 16px',
          width: '100%',
          fontSize: '1rem',
          color: theme.palette.text.primary
        },
        '.card-icon': {
          top: '4px'
        }
      }
      PayFields.customizations.placeholders = {
        '#payment_number': '1234 1234 1234 1234',
        '#expiration': 'MM / YY',
        '#payment_cvv': 'CVC',
        '#name': ' '
      }

      PayFields.ready()

      // Set setPayFieldsLoading false after 1 second to prevent flickering
      setTimeout(() => {
        setPayFieldsLoading(false)
      }, 1000)

      handleSubmit = async () => {
        setSubmitting(true)
        setPayFieldsLoading(true)
        const country = (document.querySelector('#country') as HTMLInputElement).value
        const email = (document.querySelector('#email') as HTMLInputElement).value
        const zip = (document.querySelector('#zip') as HTMLInputElement).value

        // validate the zip code
        if (!zip) {
          handleZipError(__('This field is required'))
          return
        } else if (country === 'CAN' && !/^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i.test(zip)) {
          handleZipError(__('Invalid postal code'))
          return
        } else if (country === 'USA' && !/^\d{5}$/.test(zip)) {
          handleZipError(__('Invalid zip'))
          return
        } else {
          setZipError('')
        }

        // create an optimistic activityId and create the activity on success
        activityId = randomString({ length: 32, charset: 'abcdefghijklmnopqrstuvwxyz0123456789' })

        // 90/100 characters already used
        const payrixMeta: PayrixTxnMetadata = {
          aId: activityId,
          cId: companyId,
          t: quote.is_invoice ? 'p' : 'd',
          env: process.env.DEPLOYMENT_STAGE
        }

        PayFields.config.additionalData = useToken
          ? { tokenDescription: JSON.stringify(payrixMeta) }
          : { txnDescription: JSON.stringify(payrixMeta) }

        PayFields.config.billingAddress = {
          country,
          email,
          zip: zip.toUpperCase()
        }
        PayFields.submit()
      }

      submitButton = document.querySelector('#payrix-submit-button')
      submitButton?.addEventListener('click', handleSubmit)

      // Failure Callback
      PayFields.onFailure = function (err) {
        setSubmitting(false)
        setPayFieldsLoading(false)
        const errors = (err.errors as any[]) || []
        const error15 = errors.find((error) => error.code === 15)
        if (error15) {
          if (error15.field === 'customer.email') {
            log.info('PayFields CC invalid email', { data: { error: err } })
            enqueueSnackbar(__('Please enter a valid email address'), { variant: 'error' })
          } else if (error15.msg) {
            log.warn('PayFields CC code 15 with msg', { data: { error: err }, sentry: true })
            enqueueSnackbar(error15.msg, { variant: 'error' })
          } else {
            log.warn('PayFields CC code 15 validation error', { data: { error: err }, sentry: true })
            enqueueSnackbar(__('Validation error. Please make sure all fields are complete and correct.'), {
              variant: 'error'
            })
          }
        } else {
          log.error('PayFields CC unexpected failure', { data: { error: err }, sentry: true })
          enqueueSnackbar(__('Something went wrong, please try again later.'), { variant: 'error' })
        }
      }

      // Success Callback
      PayFields.onSuccess = async function (response) {
        setActivityId(activityId)

        // For txn mode (no surcharge), PayFields returns the txnId directly.
        // For token mode (surcharge), no txnId yet — createTxnFromToken will update it later.
        const txnId = useToken ? undefined : (response?.data?.[0]?.id as string | undefined)

        // addProcessingPayment records the payment AND creates the payment activity on the invoice
        // (server-side). When the #18623 flag is off, the resolver falls back to creating just the
        // activity (legacy behavior), so this single call is correct in both modes.
        await addProcessingPaymentMutation({
          variables: {
            quoteId: quote._id,
            activityId,
            amount,
            type: 'Credit Card',
            note: note?.length > 100 ? `${note.substring(0, 100)}...` : note,
            deposit: !quote.is_invoice,
            txnId,
            source,
            surcharge,
            ...(paymentRequestId ? { paymentRequestId } : {})
          }
        })

        handleSuccessfulPayment()
      }

      PayFields.onValidationFailure = function () {
        console.log('onValidationFailure')
        setSubmitting(false)
        setPayFieldsLoading(false)
      }
    }

    document.head.appendChild(script)

    return () => {
      const PayFields = ((window as any).PayFields as any) || {}
      submitButton.removeEventListener('click', handleSubmit)
      document.head.removeChild(script)
      PayFields.unmountAll()
      ;(window as any).PayFields = undefined
    }
  }, [])

  return (
    <>
      {payFieldsLoading && <Spinner style={{ display: 'block', margin: '81px auto', width: 30 }} />}
      <Grid container item spacing={2} className={classes.root} style={{ display: payFieldsLoading ? 'none' : 'flex' }}>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="name">
            {__('Full Name')}
          </InputLabel>
          <div id="name"></div>
        </Grid>
        <Grid item xs={12} md={6}>
          <Input fullWidth label={__('Email')} id={'email'} defaultValue={quote.contact?.email} />
        </Grid>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="number">
            {__('Card Number')}
          </InputLabel>
          <div id="number"></div>
        </Grid>
        <Grid item xs={6} md={3} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="expiration">
            {__('Expiration date')}
          </InputLabel>
          <div id="expiration"></div>
        </Grid>
        <Grid item xs={6} md={3} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="cvv">
            {__('Security Code')}
          </InputLabel>
          <div id="cvv"></div>
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownSelect
            fullWidth
            label={__('Country')}
            options={[
              { label: __('United States'), value: 'USA' },
              { label: __('Canada'), value: 'CAN' }
            ]}
            variant="single"
            value={country}
            onChange={setCountry}
          />
          <input id="country" type="hidden" value={country.value} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Input
            classes={{ root: zipError ? classes.zipInputErrorRoot : undefined, input: classes.zipInput }}
            fullWidth
            label={country?.value === 'CAN' ? __('Postal Code') : __('Zip')}
            id={'zip'}
            placeholder={country?.value === 'CAN' ? 'A1A 1A1' : '12345'}
            helperTextState={'error'}
            helperText={
              zipError ? (
                <div
                  style={{ color: 'red', fontSize: '14px', fontFamily: 'sans-serif', letterSpacing: 0, lineHeight: 1 }}
                >
                  {zipError}
                </div>
              ) : (
                ''
              )
            }
          />
        </Grid>
      </Grid>
    </>
  )
}
